import type React from "react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader } from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { sanitizeBrlValue } from "../simulador-poupanca-selic";
import { RentVsBuyResponse } from "./components/RentVsBuyResponse";
import { calculateRentVsBuy } from "./utils/functions";
const calculatorKey = "alugar-vs-financiar";

interface RentVsBuyResults {
  imovel: string;
  totalAluguel: string;
  financiamento: string;
  patrimonioFinanciando: string;
  patrimonioAlugando: string;
  prazo: number;
  bestChoice: string;
  meses: number[];
  prestacoes: number[];
  alugueis: number[];
  saldosDevedores: number[];
  aportesFinanciando: number[];
  aportesAlugando: number[];
  totalAportesFinanciando: string;
  totalAportesAlugando: string;
  rentabilidade: number;
  valorizacao: number;
  igpm: number;
}

export const AlugarVsFinanciar: React.FC = () => {
  const [valorImovel, setValorImovel] = useState<string>("");
  const [valorAluguel, setValorAluguel] = useState<string>("");
  const [valorizacao, setValorizacao] = useState<string>("");
  const [igpm, setIgpm] = useState<string>("");
  const [entrada, setEntrada] = useState<string>("");
  const [custos, setCustos] = useState<string>("");
  const [prazo, setPrazo] = useState<string>("");
  const [taxaAnual, setTaxaAnual] = useState<string>("");
  const [rentabilidade, setRentabilidade] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<RentVsBuyResults | null>(null);
  const handleCalculate = () => {
    setError("");

    if (
      !valorImovel ||
      !valorAluguel ||
      !valorizacao ||
      !igpm ||
      !entrada ||
      !custos ||
      !prazo ||
      !taxaAnual ||
      !rentabilidade
    ) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    const params = {
      valorImovel: Number.parseFloat(
        valorImovel.replace(/[^0-9,-]+/g, "").replace(",", ".")
      ),
      valorAluguel: Number.parseFloat(
        valorAluguel.replace(/[^0-9,-]+/g, "").replace(",", ".")
      ),
      valorizacao: Number.parseFloat(valorizacao),
      igpm: Number.parseFloat(igpm),
      entrada: Number.parseFloat(
        entrada.replace(/[^0-9,-]+/g, "").replace(",", ".")
      ),
      custos: Number.parseFloat(
        custos.replace(/[^0-9,-]+/g, "").replace(",", ".")
      ),
      prazo: Number.parseInt(prazo),
      taxaAnual: Number.parseFloat(taxaAnual),
      rentabilidade: Number.parseFloat(rentabilidade),
    };

    const calculatedResults = calculateRentVsBuy(params);
    setResults(calculatedResults);
  };

  const handleClear = () => {
    setValorImovel("");
    setValorAluguel("");
    setValorizacao("");
    setIgpm("");
    setEntrada("");
    setCustos("");
    setPrazo("");
    setTaxaAnual("");
    setRentabilidade("");
    setResults(null);
    setError("");
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      valorImovel,
      valorAluguel,
      valorizacao,
      igpm,
      entrada,
      custos,
      prazo,
      taxaAnual,
      rentabilidade,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setValorImovel((savedData.valorImovel as string) || "");
    setValorAluguel((savedData.valorAluguel as string) || "");
    setValorizacao((savedData.valorizacao as string) || "");
    setIgpm((savedData.igpm as string) || "");
    setEntrada((savedData.entrada as string) || "");
    setCustos((savedData.custos as string) || "");
    setPrazo((savedData.prazo as string) || "");
    setTaxaAnual((savedData.taxaAnual as string) || "");
    setRentabilidade((savedData.rentabilidade as string) || "");
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card>
        <CardHeader>
          <h1 className="text-primary text-xl font-bold">
            Alugar ou financiar um imóvel?
          </h1>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
              checkCalculatorUsageLimitAndRedirect(handleCalculate);
            }}
            className="grid gap-4"
          >
            <div className="grid md:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="valorImovel">Valor do imóvel (R$)</Label>
                <div className="relative">
                  <Input
                    id="valorImovel"
                    type="text"
                    placeholder="500.000,00"
                    value={valorImovel}
                    onChange={(e) => setValorImovel(sanitizeBrlValue(e))}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="valorAluguel">Valor do aluguel (R$)</Label>
                <div className="relative">
                  <Input
                    id="valorAluguel"
                    type="text"
                    placeholder="2.000,00"
                    value={valorAluguel}
                    onChange={(e) => setValorAluguel(sanitizeBrlValue(e))}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="valorizacao">Valorização anual (%)</Label>
                <div className="relative">
                  <Input
                    id="valorizacao"
                    type="text"
                    placeholder="4,00"
                    className="pr-8"
                    value={valorizacao}
                    onChange={(e) => setValorizacao(sanitizeBrlValue(e))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="igpm">IGPM anual (%)</Label>
                <div className="relative">
                  <Input
                    id="igpm"
                    type="text"
                    placeholder="8,63"
                    className="pr-8"
                    value={igpm}
                    onChange={(e) => setIgpm(sanitizeBrlValue(e))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="entrada">Entrada (R$)</Label>
                <div className="relative">
                  <Input
                    id="entrada"
                    type="text"
                    placeholder="100.000,00"
                    value={entrada}
                    onChange={(e) => setEntrada(sanitizeBrlValue(e))}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="custos">Custos do financiamento (R$)</Label>
                <div className="relative">
                  <Input
                    id="custos"
                    type="text"
                    placeholder="15.000,00"
                    value={custos}
                    onChange={(e) => setCustos(sanitizeBrlValue(e))}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="prazo">Prazo (meses)</Label>
                <div className="relative">
                  <Input
                    id="prazo"
                    type="text"
                    placeholder="360"
                    className="pr-14"
                    value={prazo}
                    onChange={(e) => setPrazo(e.target.value)}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    meses
                  </span>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="taxaAnual">Taxa anual (%)</Label>
                <div className="relative">
                  <Input
                    id="taxaAnual"
                    type="text"
                    placeholder="7,00"
                    className="pr-8"
                    value={taxaAnual}
                    onChange={(e) => setTaxaAnual(sanitizeBrlValue(e))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rentabilidade">Rentabilidade anual (%)</Label>
                <div className="relative">
                  <Input
                    id="rentabilidade"
                    type="text"
                    placeholder="11,00"
                    className="pr-8"
                    value={rentabilidade}
                    onChange={(e) => setRentabilidade(sanitizeBrlValue(e))}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button type="submit" variant="default" className="sm:w-[150px]">
                Calcular
              </Button>
              <Button onClick={handleClear} variant="ghost">
                Limpar
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>

          {results && <RentVsBuyResponse results={results} />}
        </CardContent>
      </Card>
    </div>
  );
};
